import NotesIcon from './NotesIcon';
import DiscussionIcon from './DiscussionIcon';
import { FiChevronLeft, FiChevronRight, FiLink, FiExternalLink, FiShare2, FiSmile, FiCopy } from 'react-icons/fi';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { AddNewNodeIcon, DeleteIcon, ColorPickerIcon, FocusIcon } from './icons';

function MenuItem({ icon, children, onClick, itemStyle, hoverBg, autoFocus = false, submenu, expanded, onOpenSubmenu }) {
  return (
    <button
      type="button"
      role="menuitem"
      autoFocus={autoFocus}
      aria-haspopup={submenu ? 'menu' : undefined}
      aria-expanded={submenu ? Boolean(expanded) : undefined}
      onKeyDown={event => {
        if (submenu && event.key === 'ArrowRight') { event.preventDefault(); event.stopPropagation(); onOpenSubmenu?.(event.currentTarget); }
      }}
      style={{
        ...itemStyle,
        width: '100%',
        border: 'none',
        background: 'transparent',
        textAlign: 'left',
      }}
      onClick={(event) => {
        event.stopPropagation();
        if (submenu) onOpenSubmenu?.(event.currentTarget);
        else onClick?.();
      }}
      onMouseEnter={(event) => (event.currentTarget.style.backgroundColor = hoverBg)}
      onMouseLeave={(event) => (event.currentTarget.style.backgroundColor = 'transparent')}
    >
      {icon}
      <span>{children}</span>
      {submenu && <FiChevronRight aria-hidden="true" style={{ marginLeft: 'auto' }} />}
    </button>
  );
}

function MenuGroup({ label, children, text, border, first = false, hideLabel = false }) {
  if (!children) return null;
  return (
    <section
      aria-label={label}
      style={{
        borderTop: first ? 'none' : `1px solid ${border}`,
        marginTop: first ? 0 : 4,
        paddingTop: first ? 0 : 4,
      }}
    >
      {!hideLabel && <div
        aria-hidden="true"
        style={{
          padding: '3px 10px 2px',
          color: text,
          opacity: 0.55,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>}
      {children}
    </section>
  );
}

function TaskIcon({ text, checked = false }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        borderRadius: 3,
        border: `1.5px solid ${text}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        flexShrink: 0,
      }}
    >
      {checked ? '✓' : '+'}
    </span>
  );
}

function NodeContextMenu({
  maxHeight = 'min(664px, calc(100dvh - 76px))',
  isDarkMode,
  onDismiss,
  onAddChild,
  onAddSibling,
  onAddTaskChild,
  onDelete,
  onChangeColor,
  onChangeFont,
  onFocusBranch,
  showFocusAction = false,
  onShareIdea,
  onOpenBranch,
  onCopyText,
  onCopyLink,
  isTask = false,
  onToggleTask,
  onChangeEmoji,
  onCreateMindmapLink,
  onCreateMarkdown,
  onConvertBranchToMarkdown,
  onTidyBranch,
  onConvertBranchToMap,
  canConvertBranchToMap = false,
  canConvertBranchToMarkdown = true,
  canCreateMindmapLink = true,
  canAddSibling = true,
  canWrite = true,
  canDiscuss = false,
  onOpenDiscussion,
  canOpenInfo = false,
  onOpenInfo,
  canModerate = false,
  isModerated = false,
  onHideBranch,
  onRestoreBranch,
  canAssignTask = false,
  taskAssignees = [],
  assignedPrincipal = null,
  assignmentLoading = false,
  assignmentError = null,
  onAssignTask,
}) {
  const scrollRef = useRef(null);
  const [submenu, setSubmenu] = useState(null);
  const returnLabel = useRef(null);
  const openSubmenu = (name, trigger) => { returnLabel.current = trigger.textContent; setSubmenu(name); };
  const back = () => setSubmenu(null);
  const [scrollState, setScrollState] = useState({ above: false, below: false, overflow: false });
  const updateScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const next = { above: el.scrollTop > 2, below: el.scrollTop + el.clientHeight < el.scrollHeight - 2, overflow: el.scrollHeight > el.clientHeight + 2 };
    setScrollState(previous => Object.keys(next).every(key => previous[key] === next[key]) ? previous : next);
  };
  useLayoutEffect(() => {
    updateScroll();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateScroll);
    if (scrollRef.current) observer?.observe(scrollRef.current);
    if (scrollRef.current?.firstElementChild) observer?.observe(scrollRef.current.firstElementChild);
    window.addEventListener('resize', updateScroll);
    return () => { observer?.disconnect(); window.removeEventListener('resize', updateScroll); };
  }, [submenu]);
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    const items = [...(scrollRef.current?.querySelectorAll('[role="menuitem"]') || [])];
    if (submenu) items[0]?.focus({ preventScroll: true });
    else if (returnLabel.current) {
      items.find(item => item.textContent === returnLabel.current)?.focus({ preventScroll: true });
      returnLabel.current = null;
    }
  }, [submenu]);
  const navigate = event => {
    // The menu owns its keys, including activation and dismissal.
    event.stopPropagation();
    if (submenu && ['ArrowLeft', 'Escape'].includes(event.key)) { event.preventDefault(); event.stopPropagation(); back(); return; }
    if (event.key === 'Escape' || event.key === 'Tab') {
      event.preventDefault();
      onDismiss?.();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || event.target.tagName === 'SELECT') return;
    const items = [...scrollRef.current.querySelectorAll('[role="menuitem"]:not(:disabled)')];
    if (!items.length) return;
    event.preventDefault(); event.stopPropagation();
    const current = items.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
      : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[next].focus({ preventScroll: true });
    items[next].scrollIntoView({ block: 'nearest' });
  };
  const bg = isDarkMode ? '#1f2933' : '#ffffff';
  const border = isDarkMode ? '#4b5563' : '#e5e7eb';
  const text = isDarkMode ? '#e5e7eb' : '#111827';
  const hoverBg = isDarkMode ? '#374151' : '#f3f4f6';
  const itemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: 13,
    color: text,
    borderRadius: 4,
    minHeight: 44,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };
  const menuItemProps = { itemStyle, hoverBg };
  const markdownIcon = (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 16,
        border: `1px solid ${text}`,
        borderRadius: 3,
        fontSize: 9,
        textTransform: 'lowercase',
        flexShrink: 0,
      }}
    >
      md
    </span>
  );

  return (
    <div
      role="menu"
      aria-label="Idea actions"
      className="idea-action-menu"
      style={{
        width: 268,
        maxWidth: 'calc(100vw - 16px)',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        maxHeight,
        boxSizing: 'border-box',
        backgroundColor: bg,
        border: `1px solid ${border}`,
        borderRadius: 8,
        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3), 0 4px 6px -2px rgba(0,0,0,0.25)',
        padding: 6,
        fontFamily: '"Quicksand", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      onKeyDown={navigate}
    >
      {submenu && <button type="button" className="idea-action-menu__back" onClick={back} style={{ ...itemStyle, border: 0, background: hoverBg, flexShrink: 0 }}><FiChevronLeft aria-hidden="true" />{submenu}</button>}
      {scrollState.overflow && <button type="button" aria-disabled={!scrollState.above} aria-label="More actions above" onClick={() => scrollState.above && scrollRef.current.scrollBy({ top: -220 })} style={{ flexShrink: 0, minHeight: 32, background: hoverBg, color: text, border: 0, borderRadius: 4, opacity: scrollState.above ? 1 : .5 }}>↑ More actions</button>}
      <div ref={scrollRef} onScroll={updateScroll} style={{ overflowY: 'auto', overscrollBehavior: 'contain', minHeight: 0 }}>
      <div>
      {canWrite && (!submenu || submenu === 'Create') && (
        <MenuGroup label="Create" text={text} border={border} first hideLabel={Boolean(submenu)}>
          {!submenu && <>
          <MenuItem
            {...menuItemProps}
            autoFocus={!canOpenInfo && !canDiscuss}
            onClick={onAddChild}
            icon={<AddNewNodeIcon />}
          >
            Add idea
          </MenuItem>
          {canAddSibling && (
            <MenuItem {...menuItemProps} onClick={onAddSibling} icon={<AddNewNodeIcon />}>
              Add sibling idea (Enter / Shift+Tab)
            </MenuItem>
          )}
          <MenuItem {...menuItemProps} submenu onOpenSubmenu={trigger => openSubmenu('Create', trigger)} icon={<AddNewNodeIcon />}>More create options</MenuItem>
          </>}
          {submenu === 'Create' && <>
        {canWrite && (
          <MenuItem
            {...menuItemProps}
            onClick={onToggleTask}
            icon={<TaskIcon text={text} checked={isTask} />}
          >
            {isTask ? 'Remove task flag' : 'Convert to task'}
          </MenuItem>
        )}
          <MenuItem {...menuItemProps} onClick={onAddTaskChild} icon={<TaskIcon text={text} />}>
            Add task
          </MenuItem>
          <MenuItem {...menuItemProps} onClick={onCreateMarkdown} icon={markdownIcon}>
            Create Markdown
          </MenuItem>
          {canCreateMindmapLink && (
            <MenuItem
              {...menuItemProps}
              onClick={onCreateMindmapLink}
              icon={(
                <img
                  src="/favicon-32x32.png"
                  width="16"
                  height="16"
                  alt=""
                  aria-hidden="true"
                  style={{ display: 'block', borderRadius: 3, flexShrink: 0 }}
                />
              )}
            >
              New Map
            </MenuItem>
          )}
          </>}
        </MenuGroup>
      )}

      {!submenu && <MenuGroup label="Idea" text={text} border={border} first={!canWrite}>
        {canOpenInfo && (
          <MenuItem {...menuItemProps} autoFocus onClick={onOpenInfo} icon={<NotesIcon size={18} />}>
            Notes
          </MenuItem>
        )}
        {canDiscuss && (
          <MenuItem {...menuItemProps} autoFocus={!canOpenInfo} onClick={onOpenDiscussion} icon={<DiscussionIcon size={18} />}>
            Discuss
          </MenuItem>
        )}
        {canWrite && canAssignTask && isTask && (
          <label
            style={{
              ...itemStyle,
              alignItems: 'stretch',
              cursor: assignmentLoading ? 'wait' : 'default',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span aria-hidden="true" style={{ fontSize: 16 }}>@</span>
              <span>Responsible</span>
            </span>
            <select
              aria-label="Assign task to collaborator"
              value={assignedPrincipal?.userId || ''}
              disabled={assignmentLoading}
              onChange={(event) => {
                event.stopPropagation();
                const assignee = taskAssignees.find(
                  (candidate) => candidate.userId === event.target.value
                ) || null;
                onAssignTask?.(assignee);
              }}
              onClick={(event) => event.stopPropagation()}
              style={{
                width: '100%',
                minHeight: 34,
                borderRadius: 6,
                border: `1px solid ${border}`,
                background: bg,
                color: text,
                padding: '4px 7px',
              }}
            >
              <option value="">Unassigned</option>
              {assignedPrincipal?.userId
                && !taskAssignees.some((candidate) => candidate.userId === assignedPrincipal.userId) && (
                  <option value={assignedPrincipal.userId} disabled>
                    {assignedPrincipal.displayName || 'Former collaborator'} (unavailable)
                  </option>
                )}
              {taskAssignees.map((assignee) => (
                <option key={assignee.userId} value={assignee.userId}>
                  {assignee.selectionLabel || assignee.displayName}
                </option>
              ))}
            </select>
            {assignmentError && (
              <span role="status" style={{ fontSize: 11, opacity: 0.75 }}>
                Could not refresh the editor list. Try again when online.
              </span>
            )}
          </label>
        )}
        {canWrite && <MenuItem {...menuItemProps} submenu onOpenSubmenu={trigger => openSubmenu('Appearance', trigger)} icon={<ColorPickerIcon />}>Appearance</MenuItem>}
        <MenuItem {...menuItemProps} onClick={onCopyText} icon={<FiCopy aria-hidden="true" />}>
          Copy idea text
        </MenuItem>
      </MenuGroup>}
      {submenu === 'Appearance' && <MenuGroup label="Appearance" text={text} border={border} first hideLabel>
        {canWrite && (
          <MenuItem {...menuItemProps} onClick={onChangeColor} icon={<ColorPickerIcon />}>
            Change color
          </MenuItem>
        )}
        {canWrite && onChangeFont && <MenuItem {...menuItemProps} onClick={onChangeFont} icon={<span aria-hidden="true">Aa</span>}>Font…</MenuItem>}
        {canWrite && (
          <MenuItem {...menuItemProps} onClick={onChangeEmoji} icon={<FiSmile aria-hidden="true" />}>
            Emoji
          </MenuItem>
        )}
      </MenuGroup>}
      {!submenu && <MenuItem {...menuItemProps} submenu onOpenSubmenu={trigger => openSubmenu('Branch & links', trigger)} icon={<FiLink aria-hidden="true" />}>Branch &amp; links</MenuItem>}
      {submenu === 'Branch & links' && <MenuGroup label="Branch" text={text} border={border} first hideLabel>
        {showFocusAction && <MenuItem {...menuItemProps} onClick={onFocusBranch} icon={<FocusIcon />}>
          Focus on this branch
        </MenuItem>}
        <MenuItem {...menuItemProps} onClick={onShareIdea} icon={<FiShare2 aria-hidden="true" />}>
          Share idea
        </MenuItem>
        <MenuItem {...menuItemProps} onClick={onOpenBranch} icon={<FiExternalLink aria-hidden="true" />}>
          Open branch in new tab
        </MenuItem>
        <MenuItem {...menuItemProps} onClick={onCopyLink} icon={<FiLink aria-hidden="true" />}>
          Copy link to this branch
        </MenuItem>
        {canWrite && canConvertBranchToMap && (
          <MenuItem {...menuItemProps} onClick={onConvertBranchToMap} icon={<span aria-hidden="true">↗</span>}>
            Convert idea to its own map
          </MenuItem>
        )}
        {canWrite && (
          <MenuItem {...menuItemProps} onClick={onTidyBranch} icon={<span aria-hidden="true" style={{ fontSize: 16 }}>⌸</span>}>
            Tidy this branch
          </MenuItem>
        )}
        {canWrite && canConvertBranchToMarkdown && (
          <MenuItem {...menuItemProps} onClick={onConvertBranchToMarkdown} icon={<span aria-hidden="true" style={{ width: 18, textAlign: 'center', fontSize: 16 }}>≡</span>}>
            Turn branch into Markdown list
          </MenuItem>
        )}
      </MenuGroup>}

      {!submenu && (canModerate || canWrite) && (
        <MenuGroup label="Manage" text={text} border={border}>
          {canModerate && (
            <MenuItem
              {...menuItemProps}
              onClick={isModerated ? onRestoreBranch : onHideBranch}
              icon={<span aria-hidden="true" style={{ fontSize: 16 }}>{isModerated ? '↺' : '!'}</span>}
            >
              {isModerated ? 'Restore hidden branch' : 'Hide collaborator branch'}
            </MenuItem>
          )}
          {canWrite && (
            <MenuItem {...menuItemProps} onClick={onDelete} icon={<DeleteIcon />}>
              Delete idea
            </MenuItem>
          )}
        </MenuGroup>
      )}
      </div>
      </div>
      {scrollState.overflow && <button type="button" aria-disabled={!scrollState.below} aria-label="More actions below" onClick={() => scrollState.below && scrollRef.current.scrollBy({ top: 220 })} style={{ flexShrink: 0, minHeight: 32, background: hoverBg, color: text, border: 0, borderRadius: 4, opacity: scrollState.below ? 1 : .5 }}>↓ More actions</button>}
    </div>
  );
}

export default NodeContextMenu;
